import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import TaskEditorLivePreview from "./TaskEditorLivePreview";
import TaskEditorWorkspace from "./TaskEditorWorkspace";

interface TaskWorkspacePanelProps {
  editorState?: TaskEditorState;
  onChangeEditorState: (next: TaskEditorState) => void;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onSave: () => void;
  onEditMeta: () => void;
}

type WorkspaceTab = "edit" | "preview";

const KEY_BADGE_COLOR = "#F59E0B";

export default function TaskWorkspacePanel({
  editorState,
  onChangeEditorState,
  hasUnsavedChanges,
  isSaving,
  onSave,
  onEditMeta,
}: TaskWorkspacePanelProps) {
  const [tab, setTab] = useState<WorkspaceTab>("edit");

  if (!editorState) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: theme.vars.palette.Content.Weak,
          p: "32px",
        }}
      >
        <Typography level="body-md" sx={{ mb: "4px" }}>
          활동지를 선택하거나 만드세요
        </Typography>
        <Typography
          level="body-xs"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          좌측 목록에서 활동지를 선택하거나 [+ 새 활동지] 버튼을 눌러
          시작하세요.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "12px",
          py: "8px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              level="title-sm"
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {editorState.title || "(제목 없음)"}
            </Typography>
            {editorState.is_key_activity && (
              <Typography
                level="body-xs"
                sx={{ color: KEY_BADGE_COLOR, fontSize: "12px" }}
                title="Key 활동지"
              >
                ★
              </Typography>
            )}
            <Typography
              level="body-xs"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontSize: "11px",
              }}
            >
              {editorState.task_id || "(ID 없음)"} ·{" "}
            </Typography>
            {editorState.set_number !== undefined && (
              <Typography
                level="body-xs"
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontSize: "11px",
                }}
              >
                세트 {editorState.set_number}
              </Typography>
            )}
          </Stack>
        </Box>

        <Box
          className="meta-edit-btn"
          onClick={onEditMeta}
          sx={{
            flexShrink: 0,
            fontSize: "14px",
            lineHeight: 1,
            color: theme.vars.palette.Content.Weak,
            cursor: "pointer",
            px: "2px",
            "&:hover": {
              color: theme.vars.palette.Content.Strong,
            },
          }}
          title="메타데이터 편집"
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: "16px",
            }}
          >
            more_vert
          </span>
        </Box>

        <Button
          size="sm"
          variant="Solid"
          color="primary"
          onClick={onSave}
          disabled={!hasUnsavedChanges || isSaving}
          loading={isSaving}
          sx={{ fontSize: "12px", minHeight: "28px" }}
        >
          {hasUnsavedChanges ? "저장" : "저장됨"}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "4px",
          px: "12px",
          pt: "6px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        {(["edit", "preview"] as const).map((tabId) => {
          const isActive = tab === tabId;
          return (
            <Box
              key={tabId}
              onClick={() => setTab(tabId)}
              sx={{
                px: "10px",
                py: "6px",
                cursor: "pointer",
                borderBottom: `2px solid ${
                  isActive ? theme.vars.palette.Content.Strong : "transparent"
                }`,
                color: isActive
                  ? theme.vars.palette.Content.Strong
                  : theme.vars.palette.Content.Weak,
                fontWeight: isActive ? 700 : 500,
                fontSize: "12px",
                userSelect: "none",
              }}
            >
              {tabId === "edit" ? "편집" : "미리보기"}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {tab === "edit" ? (
          <TaskEditorWorkspace
            editorState={editorState}
            onChangeEditorState={onChangeEditorState}
          />
        ) : (
          <Box sx={{ p: "8px" }}>
            <TaskEditorLivePreview task={editorState} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
